import React, { useState } from 'react';
import { X, Lightbulb } from 'lucide-react';
import { Button } from './ui/button';

// Feature guide configurations
const FEATURE_GUIDES = {
  dropdown_standardize: {
    title: '📋 Dropdown Value Manager',
    steps: [
      {
        title: 'What is this?',
        content: 'This tool helps you clean up inconsistent values in dropdown/category fields.',
      },
      {
        title: 'Common Use Cases',
        content: 'Fix variations like "USA", "United States", "US" → "United States" or merge "Sales Rep", "Sales Representative" → "Sales"',
      },
      {
        title: 'How to Use',
        content: (
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li><strong>Keep</strong> - Keep the value as-is</li>
            <li><strong>Rename</strong> - Change to a new value</li>
            <li><strong>Merge</strong> - Combine with another value</li>
            <li><strong>Delete</strong> - Remove rows with this value</li>
          </ul>
        ),
      },
    ],
  },
  multi_checkbox: {
    title: '☑️ Multi-Value Standardizer',
    steps: [
      {
        title: 'What is this?',
        content: 'This tool handles columns with multiple values separated by semicolons (;) like tags or categories.',
      },
      {
        title: 'Example',
        content: '"Marketing; sales; Support" → "Marketing; Sales; Support" - Standardize each value in the list.',
      },
      {
        title: 'How to Use',
        content: (
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li>Each unique value from all cells is extracted</li>
            <li>Rename, merge, or delete individual values</li>
            <li>Changes apply across all cells containing that value</li>
          </ul>
        ),
      },
    ],
  },
  revenue_ranges: {
    title: '💰 Revenue Range Builder',
    steps: [
      {
        title: 'What is this?',
        content: 'Convert exact revenue numbers into meaningful buckets for better segmentation and analysis.',
      },
      {
        title: 'Example',
        content: '$1,500,000 → "1M-5M" or $50,000 → "Under 100K"',
      },
      {
        title: 'How to Use',
        content: (
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li>Select predefined ranges or customize your own</li>
            <li>A new &quot;Revenue Range&quot; column will be created</li>
            <li>Original values are preserved in the source column</li>
          </ul>
        ),
      },
    ],
  },
};

// Local storage key for tracking seen guides
const SEEN_GUIDES_KEY = 'crm_cleaner_seen_feature_guides';

// Get seen guides from localStorage
const getSeenGuides = () => {
  try {
    const stored = localStorage.getItem(SEEN_GUIDES_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

// Mark a guide as seen
const markGuideSeen = (featureKey) => {
  try {
    const seen = getSeenGuides();
    seen[featureKey] = true;
    localStorage.setItem(SEEN_GUIDES_KEY, JSON.stringify(seen));
  } catch {
    // Ignore storage errors
  }
};

// Check if guide was seen
export const hasSeenGuide = (featureKey) => {
  const seen = getSeenGuides();
  return !!seen[featureKey];
};

// Reset all guides (for testing)
export const resetAllGuides = () => {
  localStorage.removeItem(SEEN_GUIDES_KEY);
};

// Feature Guide Modal Component
export const FeatureGuideModal = ({ 
  featureKey, 
  isOpen, 
  onClose,
  onDontShowAgain 
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  
  const guide = FEATURE_GUIDES[featureKey];
  
  if (!isOpen || !guide) return null;
  
  const currentStepData = guide.steps[currentStep];
  const isLastStep = currentStep === guide.steps.length - 1;
  
  const handleClose = () => {
    if (dontShowAgain) {
      markGuideSeen(featureKey);
      onDontShowAgain?.();
    }
    onClose();
  };
  
  const handleNext = () => {
    if (isLastStep) {
      // Always mark as seen when completing all steps
      markGuideSeen(featureKey);
      onClose();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };
  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5" />
              <span className="text-sm font-medium opacity-90">First Time Guide</span>
            </div>
            <button 
              onClick={handleClose}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <h2 className="text-xl font-bold mt-2">{guide.title}</h2>
        </div>
        
        {/* Step indicator */}
        <div className="flex gap-1 px-6 pt-4">
          {guide.steps.map((_, idx) => (
            <div 
              key={idx}
              className={`h-1 flex-1 rounded-full transition-colors ${
                idx <= currentStep ? 'bg-indigo-500' : 'bg-slate-200'
              }`}
            />
          ))}
        </div>
        
        {/* Content */}
        <div className="px-6 py-5">
          <h3 className="font-semibold text-slate-900 mb-2">
            {currentStepData.title}
          </h3>
          <div className="text-slate-600">
            {currentStepData.content}
          </div>
        </div>
        
        {/* Footer */}
        <div className="px-6 pb-5 space-y-3">
          {!isLastStep && (
            <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-500 hover:text-slate-700">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span>Don&apos;t show this guide again</span>
            </label>
          )}
          
          <div className="flex gap-2">
            {currentStep > 0 && (
              <Button
                variant="outline"
                onClick={() => setCurrentStep(prev => prev - 1)}
                className="flex-1"
              >
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {isLastStep ? 'Got it!' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureGuideModal;
